import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { sichererPfad } from "../lib/weiter";
import { ApiError, type Api, type Filter, type KontoVormerkung, type MedienAnfrage, type MfaApi, type SelectQuery, type SenderDomainState, type Session, type SignupLinkAntwort, type VatCheckAntwort, type WriteOptions } from "./api";

/** 0247: Aufrufe, die etwas an Creator, Marken oder Außenstehende schicken (erst mit bestätigter Adresse des Kontos) */
const VERSAND_RPCS = new Set([
  "invitation_create", "invitation_resend", "offer_send", "media_kit_send", "invoice_send_to_talent", "invoice_send_to_brand",
  "invoice_send_dunning", "invoice_resend_correction_mail", "deal_request_invoice", "ig_share_send", "contract_send", "contract_send_copy",
  "contract_resend_signer", "contract_resend",
]);
// 0253 (K-202): Aufrufe, die nur mit einem bestimmten Schalter eine Mail nach außen schicken (Freigabe-Link und Bericht an die
// Marke per Mail, Korrektur an den Creator); ohne Mail (nur Link kopieren) bleiben sie erlaubt
const VERSAND_WENN: Record<string, (a: Record<string, unknown>) => boolean> = {
  brand_review_send: (a) => a.p_send === true,
  brand_review_renew: (a) => a.p_send === true,
  brand_report_create: (a) => a.p_send === true,
  brand_report_renew: (a) => a.p_send === true,
  content_review: (a) => a.p_approve === false,
};
import { currentLang, t } from "../lib/sprache";
import { authFehler, authFehlerArt } from "../pages/konto/auth";
import { uploadFehler } from "../lib/speicher";
import { ECHTZEIT_TABELLEN, type DatenAenderung } from "./neuladen";
import { aufSpeicherBasis, SignedUrlCache } from "./signierte-adressen";

/**
 * Supabase-Anschluss. Wird aktiv, sobald VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY gesetzt sind
 * (siehe docs/EINSPIELPLAN.md). Gleiche Semantik wie der Demo-Anschluss.
 */

type PgError = { message: string; code?: string; hint?: string | null } | null;
function check<T>(r: { data: T; error: PgError }): T {
  if (r.error) throw new ApiError(r.error.message, { code: r.error.code, hint: r.error.hint ?? undefined });
  return r.data;
}

// Der Filter-Baustein von supabase-js hat je Operator eigene Typen; hier genügt ein schmaler gemeinsamer Nenner.
type FilterBuilder = {
  eq(c: string, v: unknown): FilterBuilder; neq(c: string, v: unknown): FilterBuilder; gt(c: string, v: unknown): FilterBuilder;
  gte(c: string, v: unknown): FilterBuilder; lt(c: string, v: unknown): FilterBuilder; lte(c: string, v: unknown): FilterBuilder;
  ilike(c: string, v: string): FilterBuilder; in(c: string, v: readonly unknown[]): FilterBuilder; is(c: string, v: null | boolean): FilterBuilder;
  not(c: string, op: string, v: unknown): FilterBuilder; order(c: string, o: { ascending: boolean }): FilterBuilder; limit(n: number): FilterBuilder;
};

function applyFilters(q: FilterBuilder, filters: readonly Filter[] = []): FilterBuilder {
  for (const [c, op, v] of filters) {
    switch (op) {
      case "eq": q = q.eq(c, v); break;
      case "neq": q = q.neq(c, v); break;
      case "gt": q = q.gt(c, v); break;
      case "gte": q = q.gte(c, v); break;
      case "lt": q = q.lt(c, v); break;
      case "lte": q = q.lte(c, v); break;
      case "ilike": q = q.ilike(c, String(v)); break;
      case "in": q = q.in(c, v as unknown[]); break;
      case "is": q = q.is(c, v as null | boolean); break;
      case "not_is": q = q.not(c, "is", null); break;
    }
  }
  return q;
}

export class SupabaseApi implements Api {
  readonly mode = "supabase" as const;
  private readonly client: SupabaseClient;
  private current: Session | null = null;
  private listeners = new Set<(s: Session | null) => void>();
  private changeListeners = new Set<(a?: DatenAenderung) => void>();
  private readonly ready: Promise<void>;
  private anonClient: SupabaseClient | null = null;
  /** 0429: signierte Adressen des privaten Buckets ig-media (eine Stunde), mit der Anmeldung über die Storage-Policy */
  private readonly igMedia = new SignedUrlCache(async (paths) => {
    const r = await this.client.storage.from("ig-media").createSignedUrls(paths, 3600);
    if (r.error) throw new ApiError(r.error.message);
    const out: Record<string, string> = {};
    for (const x of r.data ?? []) if (!x.error && typeof x.path === "string" && typeof x.signedUrl === "string" && x.signedUrl) out[x.path] = x.signedUrl;
    return out;
  });

  constructor(private readonly url: string, private readonly anonKey: string) {
    this.client = createClient(url, anonKey, { auth: { persistSession: true, autoRefreshToken: true } });
    this.ready = this.client.auth.getSession().then((r) => {
      const s = r.data.session;
      this.current = s ? { userId: s.user.id, email: s.user.email ?? "", signedInAt: s.user.last_sign_in_at ?? null } : null;
      this.subscribeRealtime();
      // Gespeicherte Anmeldung eines inzwischen gelöschten Kontos (Befund 06.10.2026): Das Token gilt noch bis zum Ablauf, die
      // Seiten zeigten „angemeldet als …“ und sperrten /betrieb. Kennt der Anmeldedienst die Person nicht mehr, lokal abmelden.
      if (s) {
        void Promise.resolve().then(() => this.client.auth.getUser()).then((u) => {
          const code = (u.error as { code?: string; status?: number } | null);
          if (code && (code.code === "user_not_found" || code.status === 403)) void this.client.auth.signOut({ scope: "local" });
        }).catch(() => undefined);
      }
    });
    this.client.auth.onAuthStateChange((_e, s) => {
      const before = this.current?.userId ?? null;
      this.current = s ? { userId: s.user.id, email: s.user.email ?? "", signedInAt: s.user.last_sign_in_at ?? null } : null;
      for (const cb of this.listeners) cb(this.current);
      if ((this.current?.userId ?? null) !== before) {
        this.igMedia.clear();
        this.subscribeRealtime();
      }
    });
  }

  private realtime: ReturnType<SupabaseClient["channel"]> | null = null;
  /**
   * Echtzeit (0024): Änderungen an Chat, Glocke, Kalender, Rechnungen und Verträgen laden die Abfragen neu, und zwar nur
   * die zur jeweiligen Tabelle (data/neuladen.ts, ECHTZEIT_ABFRAGEN), gebündelt und entprellt (DataRefresh in App.tsx).
   * RLS gilt auch hier; jede Person bekommt nur Ereignisse zu Zeilen, die sie lesen darf.
   */
  private subscribeRealtime() {
    if (this.realtime) {
      void this.client.removeChannel(this.realtime);
      this.realtime = null;
    }
    if (!this.current) return;
    let ch = this.client.channel(`tk-${this.current.userId}`);
    for (const table of ECHTZEIT_TABELLEN) {
      ch = ch.on("postgres_changes" as never, { event: "*", schema: "public", table } as never, () => this.changed({ quelle: "echtzeit", tabelle: table }));
    }
    this.realtime = ch.subscribe();
  }

  whenReady(): Promise<void> {
    return this.ready;
  }
  session(): Session | null {
    return this.current;
  }
  onSessionChange(cb: (s: Session | null) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
  onDataChange(cb: (a?: DatenAenderung) => void): () => void {
    this.changeListeners.add(cb);
    return () => this.changeListeners.delete(cb);
  }
  /** Ohne Angabe: eigene Schreibaktion (lädt alles außer dem Konto); Echtzeit nennt die Tabelle. */
  private changed(a: DatenAenderung = { quelle: "lokal" }) {
    for (const cb of this.changeListeners) cb(a);
  }
  async signInWithPassword(email: string, password: string, opts?: { captchaToken?: string | null }): Promise<void> {
    const r = await this.client.auth.signInWithPassword({ email, password, ...(opts?.captchaToken ? { options: { captchaToken: opts.captchaToken } } : {}) });
    if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
  }
  /**
   * Abmelden nur auf diesem Gerät (SI-05-N): auth-js widerruft ohne Angabe alle Sitzungen des Kontos (scope "global"),
   * dann flöge die Person auch am Handy und in der installierten App hinaus. "Überall" nur auf ausdrücklichen Wunsch.
   */
  async signOut(opts?: { everywhere?: boolean }): Promise<void> {
    await this.client.auth.signOut({ scope: opts?.everywhere ? "global" : "local" });
  }
  readonly mfa: MfaApi = {
    state: async () => {
      const [aal, list] = await Promise.all([this.client.auth.mfa.getAuthenticatorAssuranceLevel(), this.client.auth.mfa.listFactors()]);
      if (aal.error) throw new ApiError(aal.error.message, { code: aal.error.code });
      if (list.error) throw new ApiError(list.error.message, { code: list.error.code });
      const factors = (list.data?.all ?? []).filter((f) => f.factor_type === "totp")
        .map((f) => ({ id: f.id, name: f.friendly_name ?? null, verified: f.status === "verified", createdAt: f.created_at }));
      return { current: aal.data?.currentLevel === "aal2" ? "aal2" : "aal1", next: aal.data?.nextLevel === "aal2" ? "aal2" : "aal1", factors };
    },
    enroll: async (name) => {
      // Unfertige Einrichtungen stören einen neuen Versuch (gleicher Name): vorher aufräumen
      const list = await this.client.auth.mfa.listFactors();
      for (const f of list.data?.all ?? []) if (f.factor_type === "totp" && f.status !== "verified") await this.client.auth.mfa.unenroll({ factorId: f.id });
      const r = await this.client.auth.mfa.enroll({ factorType: "totp", friendlyName: name });
      if (r.error || !r.data) throw new ApiError(r.error?.message ?? t("Einrichtung fehlgeschlagen.", "Setup failed."), { code: r.error?.code });
      return { factorId: r.data.id, qrSvg: r.data.totp.qr_code, secret: r.data.totp.secret };
    },
    verify: async (factorId, code) => {
      const r = await this.client.auth.mfa.challengeAndVerify({ factorId, code: code.replace(/\s+/g, "") });
      if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
      this.changed();
    },
    unenroll: async (factorId) => {
      const r = await this.client.auth.mfa.unenroll({ factorId });
      if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
    },
  };
  async signUp(email: string, password: string, opts?: { redirectTo?: string; captchaToken?: string | null; termsConsent?: boolean; language?: "de" | "en"; vormerkung?: KontoVormerkung }): Promise<{ needsConfirmation: boolean }> {
    const path = opts?.redirectTo?.startsWith("/") ? opts.redirectTo : "/einrichtung/firma";
    const r = await this.client.auth.signUp({ email, password, options: {
      emailRedirectTo: `${location.origin}${path}`,
      ...(opts?.captchaToken ? { captchaToken: opts.captchaToken } : {}),
      // Im Konto (user_metadata): Zustimmung beim Anlegen (gespeichert mit Stand wird sie nach der Bestätigung, recordConsent)
      // und Sprache der Anmelde-Mails von Supabase (supabase/auth/mailvorlagen.json wählt damit Deutsch oder Englisch)
      data: {
        ...(opts?.termsConsent ? { terms_consent_at: new Date().toISOString() } : {}),
        ...(opts?.language ? { language: opts.language } : {}),
        ...(opts?.vormerkung ? { vormerkung: opts.vormerkung } : {}),
      },
    } });
    if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
    // Schon registrierte, bestätigte Adresse: Supabase meldet Erfolg, legt nichts an und schickt keine Mail (leere identities).
    // Sonst wartet die Person vergeblich auf eine Bestätigung (Inhaber 06.10.2026), darum klar sagen und zum Anmelden schicken.
    if (!r.data.session && r.data.user && (r.data.user.identities?.length ?? 1) === 0) {
      throw new ApiError("User already registered", { code: "user_already_exists" });
    }
    return { needsConfirmation: !r.data.session };
  }
  async authProviders(): Promise<{ google: boolean }> {
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/auth/v1/settings`, { headers: { apikey: this.anonKey } });
    const body = (await res.json().catch(() => ({}))) as { external?: { google?: unknown } };
    return { google: body.external?.google === true };
  }
  async signInWithGoogle(redirectPath: string): Promise<void> {
    const path = sichererPfad(redirectPath, "/");
    const r = await this.client.auth.signInWithOAuth({ provider: "google", options: { redirectTo: `${location.origin}${path}`, queryParams: { prompt: "select_account" } } });
    if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
  }
  async signInWithGoogleIdToken(token: string, nonce: string): Promise<void> {
    const r = await this.client.auth.signInWithIdToken({ provider: "google", token, nonce });
    if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
  }
  async signupConsentGiven(): Promise<boolean> {
    const r = await this.client.auth.getUser();
    return typeof r.data.user?.user_metadata?.terms_consent_at === "string";
  }
  async requestPasswordReset(email: string, opts?: { captchaToken?: string | null; lang?: "de" | "en" }): Promise<void> {
    const r = await this.client.auth.resetPasswordForEmail(email, { redirectTo: `${location.origin}/passwort?neu=1${opts?.lang === "en" ? "&sprache=en" : ""}`, ...(opts?.captchaToken ? { captchaToken: opts.captchaToken } : {}) });
    if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
  }
  async resendSignup(email: string, opts?: { redirectTo?: string; captchaToken?: string | null }): Promise<void> {
    const path = sichererPfad(opts?.redirectTo, "/einrichtung/registrierung");
    const r = await this.client.auth.resend({ type: "signup", email, options: { emailRedirectTo: `${location.origin}${path}`, ...(opts?.captchaToken ? { captchaToken: opts.captchaToken } : {}) } });
    if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
  }
  async verifySignupCode(email: string, code: string): Promise<void> {
    const r = await this.client.auth.verifyOtp({ email, token: code.replace(/\s+/g, ""), type: "signup" });
    if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
  }
  async signupLink(action: "einloesen" | "neu" | "abmelden", token: string): Promise<SignupLinkAntwort> {
    // Ohne Anmeldung: der Schlüssel aus der Mail ist die einzige Eingabe (0243)
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/anmeldung-link`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey },
      body: JSON.stringify({ action, t: token }), referrerPolicy: "no-referrer",
    });
    const r = (await res.json().catch(() => ({}))) as Partial<SignupLinkAntwort> & { message?: string };
    if (!res.ok || typeof r.status !== "string") {
      throw new ApiError(typeof r.message === "string" ? r.message : t("Das hat gerade nicht geklappt. Bitte gleich noch einmal versuchen.", "That did not work just now. Please try again shortly."));
    }
    return { status: r.status, ...(typeof r.link === "string" ? { link: r.link } : {}) };
  }
  async kontoVormerkung(): Promise<KontoVormerkung> {
    const r = await this.client.auth.getUser();
    const v = (r.data.user?.user_metadata as { vormerkung?: unknown } | undefined)?.vormerkung;
    return v && typeof v === "object" ? (v as KontoVormerkung) : {};
  }
  async kontoHerkunftNachtragen(h: Pick<KontoVormerkung, "von" | "ref" | "utm">): Promise<void> {
    const alt = await this.kontoVormerkung();
    const r = await this.client.auth.updateUser({ data: { vormerkung: { ...alt, ...h } } });
    if (r.error) throw new ApiError(r.error.message, { code: r.error.code });
  }
  async updatePassword(password: string): Promise<void> {
    const r = await this.client.auth.updateUser({ password });
    if (r.error) {
      // 14.10.2026: geleaktes oder zu schwaches Passwort mit verständlichem Grund (gleiche Texte wie bei der Registrierung)
      const art = authFehlerArt(r.error);
      // Praxistest K-221: jede bekannte Meldung auf Deutsch bzw. Englisch, nie die rohe Meldung von Supabase
      if (art !== "unknown") throw new ApiError(authFehler(r.error, currentLang()), { code: "P0001" });
      throw new ApiError(r.error.message, { code: r.error.code });
    }
    // Praxistest K-182: nach jedem neuen Passwort alle anderen Sitzungen beenden (auch die eines Vorab-Registrierers oder nach
    // einem Passwort-Leck); diese Sitzung bleibt angemeldet
    await this.client.auth.signOut({ scope: "others" }).catch(() => undefined);
  }
  async updateEmail(email: string): Promise<{ needsConfirmation: boolean }> {
    const r = await this.client.auth.updateUser({ email }, { emailRedirectTo: `${location.origin}/agentur/profil` });
    if (r.error) {
      // Praxistest K-184: Rohmeldung von Supabase („Email address … is invalid“) in Klartext
      const c = String(r.error.code ?? ""), m = String(r.error.message ?? "");
      if (c === "email_address_invalid" || /email address .* is invalid|unable to validate email/i.test(m)) {
        throw new ApiError(t("Diese Adresse kann keine Mails empfangen oder ist nicht zulässig. Bitte eine andere E-Mail-Adresse eingeben.",
          "This address cannot receive emails or is not allowed. Please enter a different email address."), { code: "P0001" });
      }
      if (c === "email_exists" || /already (been )?registered/i.test(m)) {
        throw new ApiError(t("Zu dieser Adresse gibt es schon ein Konto.", "There is already an account with this address."), { code: "P0001" });
      }
      // Praxistest K-221: Limit, Sicherheitsprüfung, abgelaufene Sitzung usw. im Klartext
      if (authFehlerArt(r.error) !== "unknown") throw new ApiError(authFehler(r.error, currentLang()), { code: "P0001" });
      throw new ApiError(m, { code: c });
    }
    return { needsConfirmation: r.data.user?.email !== email };
  }

  /** Tabellen und Sichten ohne Spalte id (beim seitenweisen Laden bemerkt); dort bleibt es bei der Sortierung der Abfrage. */
  private readonly ohneId = new Set<string>();

  /**
   * PostgREST liefert höchstens `max_rows` Zeilen je Anfrage (Supabase: 1000). Ohne eigene Grenze holen wir seitenweise
   * nach, bis weniger als eine Seite kommt, damit Kennzahlen großer Agenturen nicht still abgeschnitten werden.
   *
   * Seiten brauchen eine eindeutige Reihenfolge: Bei gleichen Werten in der Sortierspalte (oder ganz ohne Sortierung)
   * darf die Datenbank die Zeilen je Anfrage anders anordnen, dann fehlen Zeilen oder kommen doppelt. Passt alles auf
   * eine Seite (der Normalfall), bleibt es bei einer Anfrage; sonst beginnt das Laden neu mit id als letzter Sortierung.
   */
  async select<T>(table: string, q: SelectQuery = {}): Promise<T[]> {
    type Page = PromiseLike<{ data: T[]; error: PgError }>;
    const build = (order: readonly (readonly [string, "asc" | "desc"])[]) => {
      let b = applyFilters(this.client.from(table).select(q.columns ?? "*") as unknown as FilterBuilder, q.filters);
      for (const [c, d] of order) b = b.order(c, { ascending: d !== "desc" });
      return b as unknown as { limit(n: number): unknown; range(from: number, to: number): Page };
    };
    const order = q.order ?? [];
    if (q.limit !== undefined) return check(await (build(order).limit(q.limit) as Page));
    const PAGE = 1000;
    const first = check(await build(order).range(0, PAGE - 1));
    if (first.length < PAGE) return first;
    const stabil = order.some(([c]) => c === "id") || this.ohneId.has(table) ? order : [...order, ["id", "asc"] as const];
    const seiten = async (o: typeof order): Promise<T[]> => {
      const out: T[] = [];
      for (let from = 0; ; from += PAGE) {
        const page = check(await build(o).range(from, from + PAGE - 1));
        out.push(...page);
        if (page.length < PAGE || from > 200_000) break;
      }
      return out;
    };
    if (stabil === order) return seiten(order);
    try {
      const rows = await seiten(stabil);
      // Zwischen zwei Seiten neu angelegte Zeilen können eine Zeile auf die nächste Seite schieben: nur einmal behalten
      const seen = new Set<unknown>();
      return rows.filter((r) => {
        const id = (r as { id?: unknown }).id;
        if (id === undefined) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    } catch (e) {
      // Sicht oder Tabelle ohne id (42703 undefined_column): merken und wie bisher laden
      if (e instanceof ApiError && e.code === "42703" && /\bid\b/.test(e.message)) {
        this.ohneId.add(table);
        return seiten(order);
      }
      throw e;
    }
  }
  async insert<T>(table: string, rows: Record<string, unknown> | Record<string, unknown>[], opts?: WriteOptions): Promise<T[]> {
    if (opts?.returning === false) {
      check(await (this.client.from(table).insert(rows) as unknown as PromiseLike<{ data: null; error: PgError }>));
      this.changed();
      return [];
    }
    const r = check(await this.client.from(table).insert(rows).select(opts?.columns ?? "*"));
    this.changed();
    return r as T[];
  }
  async update<T>(table: string, filters: readonly Filter[], patch: Record<string, unknown>, opts?: WriteOptions): Promise<T[]> {
    if (!filters.length) throw new ApiError("Ändern ohne Filter ist nicht erlaubt.");
    const b = applyFilters(this.client.from(table).update(patch) as unknown as FilterBuilder, filters) as unknown as { select(c?: string): PromiseLike<{ data: T[]; error: PgError }> };
    const r = check(await b.select(opts?.columns ?? "*"));
    this.changed();
    return r;
  }
  async remove(table: string, filters: readonly Filter[]): Promise<void> {
    if (!filters.length) throw new ApiError("Löschen ohne Filter ist nicht erlaubt.");
    check(await (applyFilters(this.client.from(table).delete() as unknown as FilterBuilder, filters) as unknown as PromiseLike<{ data: null; error: PgError }>));
    this.changed();
  }
  /** 0247: Stand der eigenen Adresse (kurz zwischengespeichert, nach „Mail erneut senden“ oder Korrigieren neu) */
  private bestaetigung: { at: number; v: Promise<{ verified: boolean; required: boolean } | null> } | null = null;
  vergissBestaetigung() { this.bestaetigung = null; }
  private async versandGesperrt(): Promise<boolean> {
    if (!this.bestaetigung || Date.now() - this.bestaetigung.at > 60_000) {
      this.bestaetigung = { at: Date.now(), v: this.read<{ verified: boolean; required: boolean } | null>("my_email_verification").catch(() => null) };
    }
    const s = await this.bestaetigung.v;
    return !!s && s.required && !s.verified;
  }
  async rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
    // 0247: Senden nach außen erst mit bestätigter Adresse (der Server hält solche Mails ohnehin zurück); hier mit Erklärung,
    // bevor etwas angelegt oder als „gesendet“ markiert wird
    if ((VERSAND_RPCS.has(fn) || VERSAND_WENN[fn]?.(args ?? {})) && (await this.versandGesperrt())) {
      throw new ApiError(t("Bitte bestätige zuerst deine E-Mail-Adresse. Bis dahin verschickt CreatorOffice nichts in deinem Namen. Den Bestätigungslink schicken wir dir gern noch einmal: Hinweis oben auf der Seite.",
        "Please confirm your email address first. Until then, CreatorOffice does not send anything on your behalf. We are happy to send you the confirmation link again: see the notice at the top of the page."), { code: "P0001", hint: "verify:email" });
    }
    const r = check(await this.client.rpc(fn, args ?? {}));
    this.changed();
    return r as T;
  }
  async read<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
    return check(await this.client.rpc(fn, args ?? {})) as T;
  }
  async rpcAsAnon<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
    this.anonClient ??= createClient(this.url, this.anonKey, { auth: { persistSession: false, autoRefreshToken: false, storageKey: "tk-anon" } });
    const r = check(await this.anonClient.rpc(fn, args ?? {}));
    this.changed();
    return r as T;
  }
  async upload(bucket: string, path: string, file: Blob): Promise<void> {
    const r = await this.client.storage.from(bucket).upload(path, file, { upsert: false });
    if (r.error) throw uploadFehler(r.error.message);
    this.changed();
  }
  async download(bucket: string, path: string): Promise<Blob> {
    const r = await this.client.storage.from(bucket).download(path);
    if (r.error || !r.data) throw new ApiError(r.error?.message ?? t("Datei nicht gefunden.", "File not found."));
    return r.data;
  }
  async contractDocument(token: string, mode: "sign" | "read"): Promise<Blob> {
    // Der Link steht im Rumpf (POST), nie in der Adresse der Anfrage
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/contract-document`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey }, body: JSON.stringify({ t: token, m: mode }), referrerPolicy: "no-referrer",
    });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { error?: string; message?: string | null };
      throw new ApiError(e.message ?? t("Das Dokument ließ sich nicht laden.", "The document could not be loaded."), { hint: e.error ?? undefined, code: res.status === 404 ? "P0001" : undefined });
    }
    return new Blob([await res.arrayBuffer()], { type: "application/pdf" });
  }
  async linkFile(kind: "review" | "report", token: string, attachmentId: string): Promise<Blob> {
    // Der Link steht im Rumpf (POST), nie in der Adresse der Anfrage
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/link-file`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey }, body: JSON.stringify({ k: kind, t: token, a: attachmentId }), referrerPolicy: "no-referrer",
    });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { error?: string; message?: string | null };
      throw new ApiError(e.message ?? t("Die Datei ließ sich nicht laden.", "The file could not be loaded."), { hint: e.error ?? undefined, code: res.status === 404 ? "P0001" : undefined });
    }
    return new Blob([await res.arrayBuffer()], { type: res.headers.get("content-type") ?? "application/octet-stream" });
  }

  async senderDomain(orgId: string, action: "sync" | "verify"): Promise<SenderDomainState> {
    // Mit der Anmeldung der Person (JWT); die Serverfunktion prüft sie beim Anmeldedienst, die Datenbank Rechte und Tarif
    const token = (await this.client.auth.getSession()).data.session?.access_token;
    if (!token) throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/sender-domain`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, Authorization: `Bearer ${token}` },
      body: JSON.stringify({ org: orgId, action }), referrerPolicy: "no-referrer",
    });
    const body = (await res.json().catch(() => ({}))) as { state?: SenderDomainState; error?: string; message?: string; plan?: string | null };
    if (!res.ok) {
      this.changed();
      throw senderDomainError(res.status, body);
    }
    this.changed();
    return body.state!;
  }

  async instagramConnect(talentId: string): Promise<string> {
    // Mit der Anmeldung der Person (JWT); Freischaltung, Tarif und eigenes Profil prüft die Datenbank (ig_connect_begin)
    const token = (await this.client.auth.getSession()).data.session?.access_token;
    if (!token) throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/instagram-connect`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, Authorization: `Bearer ${token}` },
      body: JSON.stringify({ talent: talentId }), referrerPolicy: "no-referrer",
    });
    const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string; message?: string | null };
    if (!res.ok || typeof body.url !== "string" || !body.url.startsWith("https://www.instagram.com/")) {
      if (body.error === "instagram:not_configured") {
        throw new ApiError(t("Die Instagram-Anmeldung ist noch nicht eingerichtet. Bitte später noch einmal versuchen.", "The Instagram login is not set up yet. Please try again later."), { code: "P0001", hint: body.error });
      }
      throw new ApiError(body.message ?? t("Die Verbindung zu Instagram ließ sich nicht starten. Bitte gleich noch einmal versuchen.", "The connection to Instagram could not be started. Please try again shortly."),
        { code: res.status === 403 ? "42501" : "P0001", hint: body.error ?? undefined });
    }
    return body.url;
  }

  async whatsappSignup(payload: { org: string; code: string; waba_id: string; phone_number_id: string } | { org: string; action: "vorlagen" }): Promise<{ display_phone: string | null; templates_created: number; template_errors: string[] }> {
    const token = (await this.client.auth.getSession()).data.session?.access_token;
    if (!token) throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/whatsapp-signup`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload), referrerPolicy: "no-referrer",
    });
    const body = (await res.json().catch(() => ({}))) as { display_phone?: string | null; templates_created?: number; template_errors?: string[]; error?: string; message?: string };
    if (!res.ok) {
      throw new ApiError(body.message ?? t("Die WhatsApp-Nummer ließ sich nicht verbinden. Bitte gleich noch einmal versuchen.", "The WhatsApp number could not be connected. Please try again shortly."),
        { code: res.status === 403 ? "42501" : "P0001", hint: body.error ?? undefined });
    }
    this.changed();
    return { display_phone: body.display_phone ?? null, templates_created: Number(body.templates_created ?? 0), template_errors: body.template_errors ?? [] };
  }

  async billing<T>(fn: "billing-change" | "billing-portal", body: Record<string, unknown>): Promise<T> {
    // Mit der Anmeldung der Person (JWT); die Serverfunktion prüft sie beim Anmeldedienst, die Datenbank das Admin-Recht
    const token = (await this.client.auth.getSession()).data.session?.access_token;
    if (!token) throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/${fn}`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, Authorization: `Bearer ${token}` },
      body: JSON.stringify(body), referrerPolicy: "no-referrer",
    });
    const r = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    this.changed();
    if (!res.ok) throw billingError(res.status, r as BillingFehler);
    return r as T;
  }

  async kontoDienste<T>(body: Record<string, unknown>): Promise<T> {
    const token = (await this.client.auth.getSession()).data.session?.access_token;
    if (!token) throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/konto-dienste`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, Authorization: `Bearer ${token}` },
      body: JSON.stringify(body), referrerPolicy: "no-referrer",
    });
    const r = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    this.changed();
    if (!res.ok) {
      throw new ApiError(typeof r.message === "string" ? r.message : t("Das hat gerade nicht geklappt. Bitte gleich noch einmal versuchen.", "That did not work just now. Please try again shortly."),
        { code: res.status === 403 ? "42501" : "P0001", hint: typeof r.error === "string" ? r.error : undefined });
    }
    return r as T;
  }

  async betriebTraffic<T>(tage: number, neu = false): Promise<T> {
    // Nur lesen: keine Änderungsmeldung (sonst lüden alle Abfragen neu)
    const token = (await this.client.auth.getSession()).data.session?.access_token;
    if (!token) throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/betrieb-traffic`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, Authorization: `Bearer ${token}` },
      body: JSON.stringify({ tage, ...(neu ? { neu: true } : {}) }), referrerPolicy: "no-referrer",
    });
    const r = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      throw new ApiError(typeof r.message === "string" ? r.message : t("Die Besucherzahlen ließen sich gerade nicht laden.", "Visitor numbers could not be loaded just now."),
        { code: res.status === 403 ? "42501" : "P0001", hint: typeof r.error === "string" ? r.error : undefined });
    }
    return r as T;
  }

  async vatCheck(body: { org: string; vat_id?: string; brand?: string; invoice?: string }): Promise<VatCheckAntwort> {
    // 0445: Prüfung bei VIES über die Serverfunktion vat-check (Rechte: app.is_staff der angemeldeten Person)
    const token = (await this.client.auth.getSession()).data.session?.access_token;
    if (!token) throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/vat-check`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, Authorization: `Bearer ${token}` },
      body: JSON.stringify(body), referrerPolicy: "no-referrer",
    });
    const r = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      throw new ApiError(typeof r.message === "string" ? r.message : t("Die USt-IdNr. ließ sich gerade nicht prüfen.", "The VAT ID could not be checked just now."),
        { code: res.status === 403 ? "42501" : "P0001", hint: typeof r.error === "string" ? r.error : undefined });
    }
    // Neues Ergebnis gespeichert: Anzeigen an Marke und Generator laden neu
    if ("check" in r && r.aus_cache !== true) this.changed();
    return r as unknown as VatCheckAntwort;
  }

  async eRechnungPruefen(body: { attachment?: string; path?: string }): Promise<Record<string, unknown>> {
    // 0448: Prüfsumme und Kurzfassung der Anlage vom Server (Rechte: Anlage sichtbar für die angemeldete Person)
    const token = (await this.client.auth.getSession()).data.session?.access_token;
    if (!token) throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/e-rechnung-pruefen`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, Authorization: `Bearer ${token}` },
      body: JSON.stringify(body), referrerPolicy: "no-referrer",
    });
    const r = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      throw new ApiError(typeof r.message === "string" ? r.message : t("Die E-Rechnung ließ sich gerade nicht serverseitig prüfen.", "The e-invoice could not be verified on the server just now."),
        { code: res.status === 403 ? "42501" : "P0001", hint: typeof r.error === "string" ? r.error : undefined });
    }
    // Ergebnis gespeichert: Anlagen und Hinweise laden neu
    if (r.again !== true) this.changed();
    return r;
  }

  async medien<T>(body: MedienAnfrage): Promise<T> {
    // Marke ohne Konto (brand_url), sonst mit der Anmeldung der Person; Fehler mit dem Hinweis-Schlüssel der Funktion
    const token = body.action === "brand_url" ? null : (await this.client.auth.getSession()).data.session?.access_token;
    if (body.action !== "brand_url" && !token) {
      throw new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "42501" });
    }
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/medien`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body), referrerPolicy: "no-referrer",
    });
    const r = (await res.json().catch(() => ({}))) as Record<string, unknown> & { error?: string; message?: string | null };
    if (!res.ok) {
      throw new ApiError(r.message ?? t("Der Medienspeicher hat nicht geantwortet. Bitte gleich noch einmal versuchen.", "The media storage did not respond. Please try again shortly."),
        { hint: r.error ?? undefined, code: res.status === 403 ? "42501" : "P0001" });
    }
    if (["finish", "abort", "start"].includes(body.action)) this.changed();
    return r as T;
  }

  mediaPut(url: string, data: Blob, opts: { onProgress?: (loaded: number) => void; signal?: AbortSignal; contentType?: string } = {}): Promise<string> {
    // XMLHttpRequest statt fetch: nur so gibt es Fortschritt beim Hochladen (auch am Handy)
    return new Promise((resolve, reject) => {
      // Schon angehalten: sofort ablehnen (abort() vor send() meldet im Browser kein Ereignis, das Versprechen hinge sonst)
      if (opts.signal?.aborted) { reject(new DOMException("abgebrochen", "AbortError")); return; }
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      if (opts.contentType) xhr.setRequestHeader("Content-Type", opts.contentType);
      xhr.upload.onprogress = (e) => opts.onProgress?.(e.loaded);
      xhr.onload = () => {
        const etag = xhr.getResponseHeader("ETag");
        if (xhr.status >= 200 && xhr.status < 300 && etag) resolve(etag);
        else reject(new ApiError(t(`Upload fehlgeschlagen (${xhr.status}).`, `Upload failed (${xhr.status}).`), { hint: xhr.status ? "media:part" : "media:network", code: String(xhr.status) }));
      };
      xhr.onerror = () => reject(new ApiError(t("Keine Verbindung. Der Upload lässt sich fortsetzen.", "No connection. The upload can be resumed."), { hint: "media:network" }));
      xhr.onabort = () => reject(new DOMException("abgebrochen", "AbortError"));
      opts.signal?.addEventListener("abort", () => xhr.abort(), { once: true });
      xhr.send(data);
    });
  }

  async igMediaUrls(paths: readonly string[]): Promise<Record<string, string>> {
    if (!this.current || !paths.length) return {};
    return this.igMedia.get(paths);
  }
  async igShareOpen(token: string, email: string): Promise<unknown> {
    // Schlüssel und Adresse stehen im Rumpf (POST), nie in der Adresse der Anfrage
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/ig-share`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey }, body: JSON.stringify({ t: token, e: email }), referrerPolicy: "no-referrer",
    });
    const body = (await res.json().catch(() => null)) as ({ error?: string; hint?: string; message?: string } & Record<string, unknown>) | null;
    if (!res.ok) {
      throw new ApiError(typeof body?.message === "string" ? body.message : t("Die Freigabe ließ sich nicht öffnen.", "The share could not be opened."),
        { hint: body?.hint ?? body?.error ?? undefined, code: res.status === 404 ? "P0001" : undefined });
    }
    if (body && body.ok === true) body.media_urls = aufSpeicherBasis(body.media_urls, this.url);
    return body;
  }

  async mediaKitImage(token: string, email: string, talentId: string, assetId?: string): Promise<Blob> {
    // Schlüssel und Adresse stehen im Rumpf (POST), nie in der Adresse der Anfrage
    const res = await fetch(`${this.url.replace(/\/+$/, "")}/functions/v1/media-kit-image`, {
      method: "POST", headers: { "Content-Type": "application/json", apikey: this.anonKey }, body: JSON.stringify({ t: token, e: email, p: talentId, ...(assetId ? { a: assetId } : {}) }), referrerPolicy: "no-referrer",
    });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(t("Das Bild ließ sich nicht laden.", "The image could not be loaded."), { hint: e.error ?? undefined, code: "P0001" });
    }
    return new Blob([await res.arrayBuffer()], { type: res.headers.get("Content-Type") ?? "image/png" });
  }
}

export interface BillingFehler { error?: string; message?: string; overage?: string[]; fields?: string[] }
/**
 * Antwort der Serverfunktionen billing-change und billing-portal (0157) als verständlicher Fehler in der Oberflächensprache.
 * Hinweise mit „billing:“, damit die Übersetzung der Datenbank-Hinweise (limit:, perm:) nicht greift.
 */
export function billingError(status: number, body: BillingFehler): ApiError {
  const code = body.error ?? "";
  const e = (text: string, hint: string) => new ApiError(text, { hint, code: "P0001" });
  switch (code) {
    case "perm:chef":
      return new ApiError(t("Abo und Käufe verwaltet nur der Admin des Kontos.", "Only the account's admin manages the subscription and purchases."), { code: "42501" });
    case "stripe:not_configured":
      return e(t("Die Online-Zahlung ist noch nicht eingerichtet. Bis dahin geht dein Tarifwunsch als Anfrage an den Plattform-Betrieb.", "Online payment is not set up yet. Until then your plan request goes to platform operations."), "billing:stripe_not_configured");
    case "price:missing":
      return e(t("Für diese Auswahl ist die Online-Buchung noch nicht eingerichtet. Stelle sie bitte als Anfrage an den Betrieb.", "Online booking is not set up for this selection yet. Please send it as a request to operations."), "billing:price_missing");
    case "portal:no_customer":
      return e(t("Für dieses Konto gibt es noch kein Zahlungskonto. Schließe zuerst ein Abo ab.", "There is no billing account for this account yet. Take out a subscription first."), "billing:no_customer");
    case "limit:plan_overage":
    case "limit:addon_overage": {
      const list = (body.overage ?? []).join("; ");
      const was = list ? ` (${list})` : "";
      return e(t(`Das passt nicht zur heutigen Nutzung${was}. Erst Creator archivieren, Team-Plätze beenden bzw. Dateien aufräumen.`,
        `This does not fit today's usage${was}. Archive creators, end team seats or clean up files first.`), "billing:overage");
    }
    case "pending:exists":
      return e(t("Es ist schon ein Wechsel zum Laufzeitende vorgemerkt. Nimm ihn zuerst zurück.", "A change at the end of the term is already scheduled. Withdraw it first."), "billing:pending");
    case "fields":
      // 0173: Rechnungsangaben für PayPal; die falschen Felder stehen im Hinweis (billing:fields:name,city)
      return e(t("Bitte die markierten Rechnungsangaben prüfen.", "Please check the highlighted billing details."), "billing:fields:" + (body.fields ?? []).filter((x) => /^[a-z_]{2,20}$/.test(x)).join(","));
    case "paypal:not_configured":
      return e(t("PayPal ist noch nicht eingerichtet. Bitte mit Karte abschließen.", "PayPal is not set up yet. Please check out with a card."), "billing:paypal_not_configured");
    case "paypal:no_resume":
      return e(t("Bei PayPal lässt sich eine Kündigung nicht zurücknehmen. Nach dem Ende schließt du einfach neu ab.", "With PayPal a cancellation cannot be withdrawn. After it ends, simply subscribe again."), "billing:paypal_no_resume");
    case "paypal:unsupported":
      return e(t("Mit PayPal geht das noch nicht. Zusatzpakete und Rabatte gibt es mit Kartenzahlung.", "This is not available with PayPal yet. Add-ons and discounts are available with card payment."), "billing:paypal_unsupported");
    case "retention:used":
      return e(t("Dieses Angebot hat eure Agentur schon einmal genutzt.", "Your agency has already used this offer."), "billing:retention_used");
    case "retention:no_subscription":
      return e(t("Das Angebot gilt für laufende Online-Abos.", "The offer applies to running online subscriptions."), "billing:retention_no_subscription");
    case "pending:none":
      return e(t("Es ist kein Wechsel vorgemerkt.", "No change is scheduled."), "billing:pending_none");
    case "plan:unchanged":
    case "addons:unchanged":
      return e(t("Das entspricht deinem aktuellen Abo.", "This matches your current subscription."), "billing:unchanged");
    case "plan:unknown":
      return e(t("Diesen Tarif gibt es nicht (mehr).", "This plan does not exist (any more)."), "billing:plan_unknown");
    // 0201: Vorlagenpaket
    case "package:owned":
      return e(t("Dieses Vorlagenpaket ist für euer Konto bereits freigeschaltet. Lade die Seite neu.", "This template pack is already unlocked for your account. Reload the page."), "billing:package_owned");
    case "package:unknown":
      return e(t("Dieses Vorlagenpaket gibt es nicht (mehr).", "This template pack does not exist (any more)."), "billing:package_unknown");
    case "change:past_due":
      return e(t("Eine Zahlung ist offen. Bitte erst unter „Rechnungen und Zahlungsart verwalten“ die Zahlungsart aktualisieren.", "A payment is outstanding. Please update the payment method under “Manage invoices and payment method” first."), "billing:past_due");
    case "change:cancelling":
      return e(t("Das Abo ist zum Laufzeitende gekündigt. Nimm die Kündigung zurück, dann kannst du wechseln.", "The subscription is cancelled at the end of the term. Withdraw the cancellation, then you can switch."), "billing:cancelling");
    case "cancel:no_subscription":
      return e(t("Es läuft kein Online-Abo.", "No online subscription is running."), "billing:cancel_no_subscription");
    case "change:no_subscription":
      return e(t("Es läuft noch kein Online-Abo. Schließe zuerst ein Abo ab.", "No online subscription is running yet. Take out a subscription first."), "billing:no_subscription");
    case "checkout:has_subscription":
      return e(t("Es läuft bereits ein Online-Abo. Lade die Seite neu, um den Tarif zu wechseln.", "An online subscription is already running. Reload the page to switch plans."), "billing:has_subscription");
    case "payment:required":
    case "payment:failed":
      return e(t("Die Zahlung wurde nicht bestätigt. Prüfe die Zahlungsart unter „Rechnungen und Zahlungsart verwalten“; bis dahin gilt der bisherige Stand.", "The payment was not confirmed. Check the payment method under “Manage invoices and payment method”; until then the previous state applies."), "billing:payment");
    case "stripe":
    case "stripe:unreachable":
    case "stripe:unknown_items":
    {
      const grund = body.message ? ` (${body.message})` : "";
      return e(t(`Der Zahlungsdienst hat die Änderung nicht angenommen. Bitte gleich noch einmal versuchen oder den Plattform-Betrieb ansprechen.${grund}`,
        `The payment provider did not accept the change. Please try again shortly or contact platform operations.${grund}`), "billing:stripe");
    }
    default:
      if (code.startsWith("auth:") || status === 401) return e(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), "auth:expired");
      return new ApiError(body.message ?? t("Das Abo ließ sich gerade nicht ändern. Bitte gleich noch einmal versuchen.", "The subscription could not be changed right now. Please try again shortly."), { hint: code ? `billing:${code.replace(/[^a-z_]/g, "_")}` : undefined, code: "P0001" });
  }
}

/** Antwort der Serverfunktion sender-domain als verständlicher Fehler (Tarifhinweis und Rechte wie bei Datenbankfehlern). */
export function senderDomainError(status: number, body: { error?: string; message?: string; plan?: string | null }): ApiError {
  const code = body.error ?? "";
  if (code.startsWith("tarif_modul:")) return new ApiError(code, { code: "P0001", hint: body.plan ?? undefined });
  if (code === "perm:chef") return new ApiError(t("Die eigene Absenderdomain richtet nur der Admin der Agentur ein.", "Only the agency's admin sets up the custom sender domain."), { code: "42501" });
  if (code === "org:read_only") return new ApiError(body.message ?? t("Diese Agentur nutzt CreatorOffice derzeit nur lesend.", "This agency uses CreatorOffice read-only at the moment."), { code: "P0001", hint: "org:read_only" });
  if (code === "resend:not_configured") {
    return new ApiError(t("Der Versanddienst ist noch nicht eingerichtet. Die Domain bleibt vorgemerkt und wird angelegt, sobald der Versand eingerichtet ist.",
      "The email service is not set up yet. The domain stays scheduled and will be created as soon as sending is set up."), { code: "P0001", hint: code });
  }
  if (code.startsWith("auth:") || status === 401) return new ApiError(t("Die Anmeldung ist abgelaufen. Bitte neu anmelden.", "Your session has expired. Please sign in again."), { code: "P0001", hint: "auth:expired" });
  if (code === "resend") return new ApiError(t(`Der Versanddienst antwortet gerade nicht: ${body.message ?? ""}`, `The email service is not responding right now: ${body.message ?? ""}`).trim(), { code: "P0001", hint: code });
  return new ApiError(body.message ?? t("Die Absenderdomain ließ sich gerade nicht prüfen.", "The sender domain could not be checked right now."), { code: "P0001", hint: code || undefined });
}
